import { useRef, useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  CardBody,
  Label,
  Input,
  FormGroup,
  CardHeader,
} from "reactstrap";
import { faCalendar } from "@fortawesome/free-solid-svg-icons";
import PortFolioService from "../../services/PortfolioService";
import Loading from "../Loading/Loading";
import "./Curriculum.scss";

// Import Advert component for Google ads
import Advert from "../Advert/Advert";

import Switch from "react-switch";
import CommonButton from "../CommonButton/CommonButton";
import swal from "sweetalert";
import { useSelector, useDispatch } from "react-redux";
import { setLanguage as setAppLanguage } from "../../reducers/LanguageReducer";
import { useHistory } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CommonMessages } from "../../constants/CommonMessages";
import CurriculumUpload from "./CurriculumUpload";

// Import button icons
import saveIcon from "../../assets/images/buttons/save.png";
import cancelIcon from "../../assets/images/buttons/cancel.png";

const Curriculum = () => {
  const dispatch = useDispatch();
  let userData = useSelector((state) => state.UserReducer.userObj);
  const lang = useSelector((state) => state.LanguageReducer.current) || "en";
  let history = useHistory();
  const datePickerRef = useRef(null);

  const [schoolName, setSchoolName] = useState();
  const [gpa, setGpa] = useState();
  const [loading, setLoading] = useState(false);
  const hasFetchedRef = useRef(false);
  const isLangFirstRunRef = useRef(true);

  const [studentValue, setStudentValue] = useState("");
  const [englishValue, setEnglishValue] = useState("");
  const [boardValue, setBoardValue] = useState("");
  const [seminarValue, setSeminarValue] = useState("");
  const [socialValue, setSocialValue] = useState("");
  const [bachelorValue, setBachelorValue] = useState("");
  const [greValue, setGreValue] = useState("");
  const [projectDate, setProjectDate] = useState("");
  const [selectedValue, setSelectedValue] = useState(10);
  const [studentLevel, setStudentLevel] = useState([]);
  const [curriculumData, setCurriculumData] = useState([]);

  // Localized alert messages
  const alerts = {
    en: {
      schoolName: "Please enter your school name",
      gpa: "Please enter your GPA",
      desiredDegree: "Please select desired degree",
      english: "Please select english test",
      plannedDate: "Please select planned enrollment date",
      social: "Please select social & leadership activities",
      board: "Please select board certification",
      seminar: "Please select project work/seminars",
      bachelor: "Please select 3 to 4 years bachelor's degree in relevant field",
      gre: "Please select GRE score of 330 or above",
      somethingWrong: "Something Went Wrong",
    },
    es: {
      schoolName: "Por favor ingresa el nombre de tu escuela",
      gpa: "Por favor ingresa tu GPA",
      desiredDegree: "Por favor selecciona el grado deseado",
      english: "Por favor selecciona el examen de inglés",
      plannedDate: "Por favor selecciona la fecha de inscripción planificada",
      social: "Por favor selecciona actividades sociales y de liderazgo",
      board: "Por favor selecciona la certificación de la junta",
      seminar: "Por favor selecciona trabajo de proyecto/seminarios",
      bachelor: "Por favor selecciona licenciatura de 3 a 4 años en el campo relevante",
      gre: "Por favor selecciona puntaje GRE de 330 o superior",
      somethingWrong: "Algo salió mal",
    },
  };
  const tAlert = (key) => (alerts[lang] || alerts.en)[key];

  // Normalize API values to UI enum values regardless of language
  const normalizeValue = (key, val) => {
    if (!val) return "";
    const v = String(val).trim();
    switch (key) {
      case "desired_degree": {
        const s = v.toLowerCase();
        if (s.includes("undergraduate") || s.includes("pregrado") || s.includes("licenciatura") || s.includes("de licenciatura")) {
          return "Undergraduate";
        }
        if (s.includes("graduate") || s.includes("posgrado") || s.includes("graduado")) {
          return "Graduate";
        }
        return v; // fallback
      }
      case "english_test": {
        const s = v.toLowerCase();
        if (s.includes("planned") || s.includes("planificado") || s.includes("planificada")) {
          return "Planned";
        }
        if (s.includes("taken") || s.includes("tomado") || s.includes("tomada")) {
          return "Taken";
        }
        return v;
      }
      case "yn": {
        const s = v.toLowerCase();
        if (s === "sí" || s === "si" || s === "yes") return "Yes";
        if (s === "no") return "No";
        return v;
      }
      default:
        return v;
    }
  };

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      getCurriculum();
    }
  }, []);

  // Re-fetch when language changes (skip initial mount)
  useEffect(() => {
    if (isLangFirstRunRef.current) {
      isLangFirstRunRef.current = false;
      return;
    }
    getCurriculum();
  }, [lang]);

  useEffect(() => {
    if (studentValue) {
      localStorage.setItem("studentValue", studentValue);
    }
  }, [studentValue]);

  const getCurriculum = () => {
    setLoading(true);
    PortFolioService.getCurriculum()
      .then((data) => {
        const curriculums = data?.data?.curriculums || [];
        setCurriculumData(curriculums);

        setSchoolName(curriculums?.current_school_name || "");
        setGpa(curriculums?.gpa_score || "");
        setSelectedValue(curriculums?.gpa_scale || 10);
        setStudentValue(normalizeValue("desired_degree", curriculums?.desired_degree || ""));
        setEnglishValue(normalizeValue("english_test", curriculums?.english_test || ""));
        setProjectDate(parseDate(curriculums?.planned_enrollment_date) || "");
        setSocialValue(normalizeValue("yn", curriculums?.social_leadership_activities || ""));

        setBoardValue(normalizeValue("yn", curriculums?.board_certification || ""));
        setSeminarValue(normalizeValue("yn", curriculums?.project_work_seminar || ""));
        setBachelorValue(normalizeValue("yn", curriculums?.relevant_degree || ""));
        setGreValue(normalizeValue("yn", curriculums?.gre_above_330 || ""));

        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  const handleStudentChange = (e) => {
    setStudentValue(e.target.value);
  };

  const handleEnglishChange = (e) => {
    setEnglishValue(e.target.value);
  };

  const handleBoardChange = (e) => {
    setBoardValue(e.target.value);
  };

  const handleSeminarChange = (e) => {
    setSeminarValue(e.target.value);
  };

  const handleSocialChange = (e) => {
    setSocialValue(e.target.value);
  };

  const handleBachelorChange = (e) => {
    setBachelorValue(e.target.value);
  };

  const handleGREScoreChange = (e) => {
    setGreValue(e.target.value);
  };

  const handleLanguage = (checked) => {
    const selectedLanguage = checked ? "es" : "en";
    dispatch(setAppLanguage(selectedLanguage));
  };

  const parseDate = (dateString) => {
    if (
      !dateString ||
      dateString === "-" ||
      !/^\d{2}-\d{2}-\d{4}$/.test(dateString)
    ) {
      return null;
    }
    const [day, month, year] = dateString.split("-");
    return new Date(year, month - 1, day);
  };

  const formatDate = (date) => {
    if (!(date instanceof Date) || isNaN(date)) {
      return "";
    }

    const day = date
      .getDate()
      .toString()
      .padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleDropdownChange = (e) => {
    setSelectedValue(e.target.value);
  };

  const handleIconClick = () => {
    if (datePickerRef.current) {
      datePickerRef.current.setOpen(true);
    }
  };

  const handleCurriculumSubmit = () => {
    if (!schoolName) {
      swal("", tAlert("schoolName"), "error");
      return;
    }
    if (!gpa) {
      swal("", tAlert("gpa"), "error");
      return;
    }
    if (!studentValue) {
      swal("", tAlert("desiredDegree"), "error");
      return;
    }

    if (studentValue === "Undergraduate") {
      if (!englishValue) {
        swal("", tAlert("english"), "error");
        return;
      }
      if (!projectDate) {
        swal("", tAlert("plannedDate"), "error");
        return;
      }
      if (!socialValue) {
        swal("", tAlert("social"), "error");
        return;
      }
    }

    if (studentValue === "Graduate") {
      if (!englishValue) {
        swal("", tAlert("english"), "error");
        return;
      }
      if (!boardValue) {
        swal("", tAlert("board"), "error");
        return;
      }
      if (!projectDate) {
        swal("", tAlert("plannedDate"), "error");
        return;
      }
      if (!seminarValue) {
        swal("", tAlert("seminar"), "error");
        return;
      }
      if (!socialValue) {
        swal("", tAlert("social"), "error");
        return;
      }
      if (!bachelorValue) {
        swal("", tAlert("bachelor"), "error");
        return;
      }
      if (!greValue) {
        swal("", tAlert("gre"), "error");
        return;
      }
    }
    let curriculum = {
      user_id: userData?.id,
      current_school_name: schoolName,
      gpa_score: parseFloat(gpa),
      gpa_scale: parseInt(selectedValue),
      desired_degree: studentValue,
      english_test: englishValue,
      planned_enrollment_date: formatDate(projectDate),
      social_leadership_activities: socialValue,
    };
    if (studentValue === "Graduate") {
      curriculum.board_certification = boardValue;
      curriculum.project_work_seminar = seminarValue;
      curriculum.relevant_degree = bachelorValue;
      curriculum.gre_above_330 = greValue;
    }
    if (studentValue === "Undergraduate") {
      curriculum.board_certification = "";
      curriculum.project_work_seminar = "";
      curriculum.relevant_degree = "";
      curriculum.gre_above_330 = "";
    }
    setLoading(true);
    PortFolioService.saveCurriculum({ curriculum })
      .then((data) => {
        if (data.status === 200) {
          setLoading(false);
          history.push("/student/curriculum-upload");
        }
      })
      .catch((error) => {
        setLoading(false);
        const errorMessage =
          error?.response?.data?.error || tAlert("somethingWrong");
        swal("", errorMessage, "error");
      });
  };

  return (
    <div className="curriculum-page">
      <div className="curriculum-ad-header">
        <div className="header-content">
          <div className="ad-section-centered">
            <Advert />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="curriculum-content">
        <div className="container-fluid">
          <div className="curriculum-container">
            <Row>
              <Col lg="12" className="mx-auto">
                <div className="curriculum-card">
                  <div className="card-body">
                    {/* Title and Toggle Section */}
                    <div className="title-section">
                      <h1 className="title-main">{lang === "es" ? "Mi plan de estudios" : "My Curriculum"}</h1>
                      {/* <Switch
                        onChange={handleLanguage}
                        checked={lang === "es"}
                        width={52}
                        height={24}
                        handleDiameter={18}
                        onColor="#28131A"
                        offColor="#049804"
                        uncheckedIcon={
                          <div className="switchIncon uncheckIcon">EN</div>
                        }
                        checkedIcon={
                          <div className="switchIncon checkIcon">ES</div>
                        }
                        className="langSwitch"
                      /> */}
                    </div>
                    {loading ? (
                      <div className="loading-container">
                        <Loading />
                      </div>
                    ) : (
                      <>
                        {/* All the existing form content remains the same */}
                        <Row>
                          <Col lg="6">
                            <FormGroup className="position-relative">
                              <Label
                                for="currentSchoolName"
                                className="labelBold"
                              >
                                {lang === "es"
                                  ? "Nombre de la escuela actual"
                                  : "Current School Name"}
                                <span className="required">*</span>
                              </Label>
                              <Input
                                required
                                type="text"
                                name="school"
                                id="school"
                                  placeholder={
                                    lang === "es"
                                      ? "Ingrese el nombre de su escuela actual"
                                      : "Enter your current school name"
                                  }
                                value={schoolName}
                                onChange={(e) => setSchoolName(e.target.value)}
                                autoComplete="off"
                                className="form-input"
                              />
                            </FormGroup>
                          </Col>
                          <Col lg="6">
                            <Row>
                              <Col lg="4">
                                <FormGroup className="position-relative">
                                  <Label for="gpaScore" className="labelBold">
                                    {lang === "es"
                                      ? "Puntuación GPA"
                                      : "GPA Score"}
                                    <span className="required">*</span>
                                  </Label>
                                  <Input
                                    required
                                    type="number"
                                    name="gpa"
                                    id="gpa"
                                      placeholder={
                                        lang === "es"
                                          ? "Ingrese su promedio"
                                          : "Enter your GPA"
                                      }
                                    value={gpa}
                                    onChange={(e) => setGpa(e.target.value)}
                                    autoComplete="off"
                                    className="form-input"
                                  />
                                </FormGroup>
                              </Col>
                              <Col
                                lg="1"
                                className="d-flex align-items-center mt-4"
                              >
                                <span className="out-of-text">
                                  {lang === "es" ? "Fuera de" : "Out of"}
                                </span>
                              </Col>
                              <Col lg="4 mt-4">
                                <FormGroup className="position-relative">
                                  <Input
                                    type="select"
                                    name="gpaOutOf"
                                    id="gpaOutOf"
                                    value={selectedValue}
                                    onChange={handleDropdownChange}
                                    className="form-select"
                                  >
                                    <option value={4}>4</option>
                                    <option value={10}>10</option>
                                  </Input>
                                </FormGroup>
                              </Col>
                            </Row>
                          </Col>

                          {/* Continue with all the existing form fields but with modern classes */}
                          <Col lg="6">
                            <FormGroup className="position-relative">
                              <Label
                                className="labelBold"
                                style={{ marginBottom: "20px" }}
                              >
                                {lang === "es"
                                  ? "Grado deseado"
                                  : "Desired Degree"}
                                <span className="required">*</span>
                              </Label>
                              <div className="radio-group">
                                <FormGroup check inline className="radio-item">
                                  <Label check>
                                    <Input
                                      type="radio"
                                      name="desiredDegree"
                                      value="Undergraduate"
                                      checked={studentValue === "Undergraduate"}
                                      onChange={handleStudentChange}
                                    />{" "}
                                    {lang === "es"
                                      ? "De licenciatura"
                                      : "Undergraduate"}
                                  </Label>
                                </FormGroup>
                                <FormGroup check inline className="radio-item">
                                  <Label check>
                                    <Input
                                      type="radio"
                                      name="desiredDegree"
                                      value="Graduate"
                                      checked={studentValue === "Graduate"}
                                      onChange={handleStudentChange}
                                    />{" "}
                                    {lang === "es"
                                      ? "Graduado"
                                      : "Graduate"}
                                  </Label>
                                </FormGroup>
                              </div>
                            </FormGroup>
                          </Col>

                          {/* All conditional form fields remain the same with updated classes */}
                          {studentValue === "Undergraduate" && (
                            <>
                              <Col lg="6">
                                <FormGroup className="position-relative">
                                  <Label
                                    className="labelBold"
                                    style={{ marginBottom: "20px" }}
                                  >
                                    {lang === "es"
                                      ? "Examen de inglés"
                                      : "English Test"}
                                    <span className="required">*</span>
                                  </Label>
                                  <div className="radio-group">
                                    <FormGroup
                                      check
                                      inline
                                      className="radio-item"
                                    >
                                      <Label check>
                                        <Input
                                          type="radio"
                                          name="englishTest"
                                          value="Planned"
                                          checked={englishValue === "Planned"}
                                          onChange={handleEnglishChange}
                                        />{" "}
                                        {lang === "es"
                                          ? "Planificado"
                                          : "Planned"}
                                      </Label>
                                    </FormGroup>
                                    <FormGroup
                                      check
                                      inline
                                      className="radio-item"
                                    >
                                      <Label check>
                                        <Input
                                          type="radio"
                                          name="englishTest"
                                          value="Taken"
                                          checked={englishValue === "Taken"}
                                          onChange={handleEnglishChange}
                                        />{" "}
                                        {lang === "es" ? "Tomado" : "Taken"}
                                      </Label>
                                    </FormGroup>
                                  </div>
                                </FormGroup>
                              </Col>

                              <Col lg="6">
                                <FormGroup className="position-relative">
                                  <Label
                                    className="labelBold"
                                    style={{ marginBottom: "20px" }}
                                  >
                                    {lang === "es"
                                      ? "Fecha de inscripción planificada"
                                      : "Planned Enrollment Date"}
                                    <span className="required">*</span>
                                  </Label>
                                  <br />
                                  <div className="date-picker-wrapper">
                                    <DatePicker
                                      selected={projectDate}
                                      onChange={(date) => setProjectDate(date)}
                                      dateFormat="yyyy-MM-dd"
                                      minDate={new Date()}
                                      placeholderText={
                                        lang === "es"
                                          ? "Selecciona la fecha de tu proyecto"
                                          : "Select your project date"
                                      }
                                      className="form-input custom-datepicker"
                                      ref={datePickerRef}
                                    />
                                    <FontAwesomeIcon
                                      icon={faCalendar}
                                      className="calendar-icon"
                                      onClick={handleIconClick}
                                    />
                                  </div>
                                </FormGroup>
                              </Col>

                              <Col lg="6">
                                <FormGroup className="position-relative">
                                  <Label
                                    className="labelBold"
                                    style={{ marginBottom: "20px" }}
                                  >
                                    {lang === "es"
                                      ? "Actividades sociales y de liderazgo"
                                      : "Social & Leadership Activities"}
                                    <span className="required">*</span>
                                  </Label>
                                  <div className="radio-group">
                                    <FormGroup
                                      check
                                      inline
                                      className="radio-item"
                                    >
                                      <Label check>
                                        <Input
                                          type="radio"
                                          name="socialActivities"
                                          value="Yes"
                                          checked={socialValue === "Yes"}
                                          onChange={handleSocialChange}
                                        />{" "}
                                        {lang === "es" ? "Sí" : "Yes"}
                                      </Label>
                                    </FormGroup>
                                    <FormGroup
                                      check
                                      inline
                                      className="radio-item"
                                    >
                                      <Label check>
                                        <Input
                                          type="radio"
                                          name="socialActivities"
                                          value="No"
                                          checked={socialValue === "No"}
                                          onChange={handleSocialChange}
                                        />{" "}
                                        {lang === "es" ? "No" : "No"}
                                      </Label>
                                    </FormGroup>
                                  </div>
                                </FormGroup>
                              </Col>
                            </>
                          )}

                          {/* All Graduate fields with similar styling updates */}
                          {studentValue === "Graduate" && (
                            <>
                              {/* All graduate form fields with modern classes */}
                              <Col lg="6">
                                <FormGroup className="position-relative">
                                  <Label
                                    className="labelBold"
                                    style={{ marginBottom: "20px" }}
                                  >
                                    {lang === "es"
                                      ? "Examen de inglés"
                                      : "English Test"}
                                    <span className="required">*</span>
                                  </Label>
                                  <div className="radio-group">
                                    <FormGroup
                                      check
                                      inline
                                      className="radio-item"
                                    >
                                      <Label check>
                                        <Input
                                          type="radio"
                                          name="englishTest"
                                          value="Planned"
                                          checked={englishValue === "Planned"}
                                          onChange={handleEnglishChange}
                                        />{" "}
                                        {lang === "es"
                                          ? "Planificado"
                                          : "Planned"}
                                      </Label>
                                    </FormGroup>
                                    <FormGroup
                                      check
                                      inline
                                      className="radio-item"
                                    >
                                      <Label check>
                                        <Input
                                          type="radio"
                                          name="englishTest"
                                          value="Taken"
                                          checked={englishValue === "Taken"}
                                          onChange={handleEnglishChange}
                                        />{" "}
                                        {lang === "es" ? "Tomado" : "Taken"}
                                      </Label>
                                    </FormGroup>
                                  </div>
                                </FormGroup>
                              </Col>

                              <Col lg="6">
                                <FormGroup className="position-relative">
                                  <Label
                                    className="labelBold"
                                    style={{ marginBottom: "20px" }}
                                  >
                                    {lang === "es"
                                      ? "Certificación de la Junta"
                                      : "Board Certification"}
                                    <span className="required">*</span>
                                  </Label>
                                  <div className="radio-group">
                                    <FormGroup
                                      check
                                      inline
                                      className="radio-item"
                                    >
                                      <Label check>
                                        <Input
                                          type="radio"
                                          name="boardCertification"
                                          value="Yes"
                                          checked={boardValue === "Yes"}
                                          onChange={handleBoardChange}
                                        />{" "}
                                        {lang === "es" ? "Sí" : "Yes"}
                                      </Label>
                                    </FormGroup>
                                    <FormGroup
                                      check
                                      inline
                                      className="radio-item"
                                    >
                                      <Label check>
                                        <Input
                                          type="radio"
                                          name="boardCertification"
                                          value="No"
                                          checked={boardValue === "No"}
                                          onChange={handleBoardChange}
                                        />{" "}
                                        {lang === "es" ? "No" : "No"}
                                      </Label>
                                    </FormGroup>
                                  </div>
                                </FormGroup>
                              </Col>

                              <Col lg="6">
                                <FormGroup className="position-relative">
                                  <Label
                                    className="labelBold"
                                    style={{ marginBottom: "20px" }}
                                  >
                                    {lang === "es"
                                      ? "Fecha de inscripción planificada"
                                      : "Planned Enrollment Date"}
                                    <span className="required">*</span>
                                  </Label>
                                  <br />
                                  <div className="date-picker-wrapper">
                                    <DatePicker
                                      selected={projectDate}
                                      onChange={(date) => setProjectDate(date)}
                                      dateFormat="yyyy-MM-dd"
                                      minDate={new Date()}
                                      placeholderText={
                                        lang === "es"
                                          ? "Selecciona la fecha de tu proyecto"
                                          : "Select your project date"
                                      }
                                      className="form-input custom-datepicker"
                                      ref={datePickerRef}
                                    />
                                    <FontAwesomeIcon
                                      icon={faCalendar}
                                      className="calendar-icon"
                                      onClick={handleIconClick}
                                    />
                                  </div>
                                </FormGroup>
                              </Col>

                              <Col lg="6">
                                <FormGroup className="position-relative">
                                  <Label
                                    className="labelBold"
                                    style={{ marginBottom: "20px" }}
                                  >
                                    {lang === "es"
                                      ? "Trabajo de proyecto/seminarios"
                                      : "Project Work/Seminars"}
                                    <span className="required">*</span>
                                  </Label>
                                  <div className="radio-group">
                                    <FormGroup
                                      check
                                      inline
                                      className="radio-item"
                                    >
                                      <Label check>
                                        <Input
                                          type="radio"
                                          name="projectWork"
                                          value="Yes"
                                          checked={seminarValue === "Yes"}
                                          onChange={handleSeminarChange}
                                        />{" "}
                                        {lang === "es" ? "Sí" : "Yes"}
                                      </Label>
                                    </FormGroup>
                                    <FormGroup
                                      check
                                      inline
                                      className="radio-item"
                                    >
                                      <Label check>
                                        <Input
                                          type="radio"
                                          name="projectWork"
                                          value="No"
                                          checked={seminarValue === "No"}
                                          onChange={handleSeminarChange}
                                        />{" "}
                                        {lang === "es" ? "No" : "No"}
                                      </Label>
                                    </FormGroup>
                                  </div>
                                </FormGroup>
                              </Col>

                              <Col lg="6">
                                <FormGroup className="position-relative">
                                  <Label
                                    className="labelBold"
                                    style={{ marginBottom: "20px" }}
                                  >
                                    {lang === "es"
                                      ? "Actividades sociales y de liderazgo"
                                      : "Social & Leadership Activities"}
                                    <span className="required">*</span>
                                  </Label>
                                  <div className="radio-group">
                                    <FormGroup
                                      check
                                      inline
                                      className="radio-item"
                                    >
                                      <Label check>
                                        <Input
                                          type="radio"
                                          name="socialActivities"
                                          value="Yes"
                                          checked={socialValue === "Yes"}
                                          onChange={handleSocialChange}
                                        />{" "}
                                        {lang === "es" ? "Sí" : "Yes"}
                                      </Label>
                                    </FormGroup>
                                    <FormGroup
                                      check
                                      inline
                                      className="radio-item"
                                    >
                                      <Label check>
                                        <Input
                                          type="radio"
                                          name="socialActivities"
                                          value="No"
                                          checked={socialValue === "No"}
                                          onChange={handleSocialChange}
                                        />{" "}
                                        {lang === "es" ? "No" : "No"}
                                      </Label>
                                    </FormGroup>
                                  </div>
                                </FormGroup>
                              </Col>

                              <Col lg="6">
                                <FormGroup className="position-relative">
                                  <Label
                                    className="labelBold"
                                    style={{ marginBottom: "20px" }}
                                  >
                                    {lang === "es"
                                      ? "Licenciatura de 3 a 4 años en el campo relevante"
                                      : "3 to 4 Years Bachelor's Degree in Relevant Field"}
                                    <span className="required">*</span>
                                  </Label>
                                  <div className="radio-group">
                                    <FormGroup
                                      check
                                      inline
                                      className="radio-item"
                                    >
                                      <Label check>
                                        <Input
                                          type="radio"
                                          name="bachelorDegree"
                                          value="Yes"
                                          checked={bachelorValue === "Yes"}
                                          onChange={handleBachelorChange}
                                        />{" "}
                                        {lang === "es" ? "Sí" : "Yes"}
                                      </Label>
                                    </FormGroup>
                                    <FormGroup
                                      check
                                      inline
                                      className="radio-item"
                                    >
                                      <Label check>
                                        <Input
                                          type="radio"
                                          name="bachelorDegree"
                                          value="No"
                                          checked={bachelorValue === "No"}
                                          onChange={handleBachelorChange}
                                        />{" "}
                                        {lang === "es" ? "No" : "No"}
                                      </Label>
                                    </FormGroup>
                                  </div>
                                </FormGroup>
                              </Col>

                              <Col lg="6">
                                <FormGroup className="position-relative">
                                  <Label
                                    className="labelBold"
                                    style={{ marginBottom: "20px" }}
                                  >
                                    {lang === "es"
                                      ? "Puntaje GRE de 330 o superior"
                                      : "GRE Score of 330 or Above"}
                                    <span className="required">*</span>
                                  </Label>
                                  <div className="radio-group">
                                    <FormGroup
                                      check
                                      inline
                                      className="radio-item"
                                    >
                                      <Label check>
                                        <Input
                                          type="radio"
                                          name="greScore"
                                          value="Yes"
                                          checked={greValue === "Yes"}
                                          onChange={handleGREScoreChange}
                                        />{" "}
                                        {lang === "es" ? "Sí" : "Yes"}
                                      </Label>
                                    </FormGroup>
                                    <FormGroup
                                      check
                                      inline
                                      className="radio-item"
                                    >
                                      <Label check>
                                        <Input
                                          type="radio"
                                          name="greScore"
                                          value="No"
                                          checked={greValue === "No"}
                                          onChange={handleGREScoreChange}
                                        />{" "}
                                        {lang === "es" ? "No" : "No"}
                                      </Label>
                                    </FormGroup>
                                  </div>
                                </FormGroup>
                              </Col>
                            </>
                          )}
                        </Row>
                      </>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </div>
      </div>

      {/* Action Buttons Section */}
      <div className="button-row">
        <button
          className="action-btn cancel-btn"
          onClick={() => history.goBack()}
        >
          <img src={cancelIcon} alt="Cancel" className="btn-icon" />
          {lang === "es" ? "Cancelar" : "Cancel"}
        </button>
        <button
          className="action-btn save-btn"
          onClick={() => handleCurriculumSubmit()}
        >
          <img src={saveIcon} alt="Save" className="btn-icon" />
          {lang === "es" ? "Guardar y siguiente" : "Save & Next"}
        </button>
      </div>
    </div>
  );
};

export default Curriculum;
